import React, { useMemo } from 'react';
import { Autocomplete, TextField, Box, Typography } from '@mui/material';
import { POPULAR_CURRENCIES } from '../../utils/currencies';

// Currency picker built on MUI Autocomplete: directly selectable (click the field → the full
// list opens → click an option) like the country/region Select, AND searchable (type to filter).
// Replaces the previous Select-with-embedded-search, whose ListSubheader/stopPropagation hack
// broke click-to-select (options only became selectable after typing).
const POPULAR_CODES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY'];

const CurrencySelector = ({
  value = 'USD',
  onChange,
  label = 'Currency',
  helperText = '',
  required = false,
  disabled = false,
  fullWidth = true,
  size = 'medium'
}) => {
  // Order so popular currencies group first (Autocomplete groupBy needs options pre-sorted by group).
  const options = useMemo(() => {
    const withGroup = POPULAR_CURRENCIES.map(c => ({
      ...c,
      group: POPULAR_CODES.includes(c.code) ? 'Popular Currencies' : 'All Currencies'
    }));
    const rank = c => (c.group === 'Popular Currencies' ? POPULAR_CODES.indexOf(c.code) : 1000);
    return withGroup.sort((a, b) => rank(a) - rank(b));
  }, []);

  const selected = useMemo(
    () => options.find(c => c.code === value) || null,
    [options, value]
  );

  return (
    <Autocomplete
      fullWidth={fullWidth}
      disabled={disabled}
      size={size}
      options={options}
      value={selected}
      groupBy={(option) => option.group}
      disableClearable
      autoHighlight
      isOptionEqualToValue={(option, val) => option.code === val.code}
      getOptionLabel={(option) => (option ? `${option.code} — ${option.name}` : '')}
      onChange={(event, newValue) => {
        if (newValue && onChange) onChange(newValue.code);
      }}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.code}>
          <Typography sx={{ mr: 1, fontSize: '1.2em' }}>{option.flag}</Typography>
          <Typography sx={{ fontWeight: 500, mr: 1 }}>{option.code}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
            {option.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">{option.symbol}</Typography>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          helperText={helperText || undefined}
        />
      )}
    />
  );
};

export default CurrencySelector;
